import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/dryveda", label: "DryVeda" },
  { to: "/export-logistics", label: "Export & Logistics" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-forest/70 backdrop-blur-xl" data-testid="navbar">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <img src="/images/logo.png" alt="Nishmayy Global logo" className="h-10 w-10 object-contain" />
          <span className="font-serif text-xl tracking-wide text-cream md:text-2xl">
            Nishmayy <span className="text-gold">Global</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `group relative text-sm tracking-wide transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-sage hover:text-cream"
                }`
              }
            >
              {l.label}
              <span
                className={`absolute -bottom-1 left-0 h-px bg-gold transition-all duration-300 ${
                  location.pathname === l.to ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="group flex items-center gap-2 border border-gold bg-gold px-5 py-2.5 text-sm font-semibold tracking-wide text-forest transition-colors duration-300 hover:bg-transparent hover:text-gold"
          >
            Partner With Us
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          className="text-cream lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="overflow-hidden border-t border-white/10 bg-forest lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 font-serif text-2xl ${isActive ? "text-gold" : "text-cream"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
            <div className="flex items-center gap-4 border-t border-white/10 px-6 py-5" data-testid="nav-mobile-brand">
              <img src="/images/logo.png" alt="Nishmayy Global emblem" className="h-12 w-12 object-contain" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Nishmayy Global</span>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
